import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  FileText, Car, ListChecks, BadgeCheck, Database, Settings as SettingsIcon,
  Radar, Library, Users, LogOut, Menu, X, Languages,
} from "lucide-react";

import Acts from "./sections/Acts";
import Cards from "./sections/Cards";
import Questionnaire from "./sections/Questionnaire";
import Brands from "./sections/Brands";
import VinRecords from "./sections/VinRecords";
import SiteSettings from "./sections/SiteSettings";
import DecoderSettings from "./sections/DecoderSettings";
import DecoderRefs from "./sections/DecoderRefs";
import TranslationSettings from "./sections/TranslationSettings";
import Managers from "./sections/Managers";

const NAV = [
  { key: "acts", label: "Акты", icon: FileText, comp: Acts },
  { key: "cards", label: "Карточки авто", icon: Car, comp: Cards },
  { key: "questionnaire", label: "Опросник", icon: ListChecks, comp: Questionnaire },
  { key: "brands", label: "Марки и логотипы", icon: BadgeCheck, comp: Brands },
  { key: "vin", label: "База VIN", icon: Database, comp: VinRecords },
  { key: "settings", label: "Настройки сайта", icon: SettingsIcon, comp: SiteSettings },
  { key: "decoder", label: "Настройки декодера", icon: Radar, comp: DecoderSettings },
  { key: "decoder-refs", label: "Справочники декодера", icon: Library, comp: DecoderRefs },
  { key: "translation", label: "Перевод (DeepL)", icon: Languages, comp: TranslationSettings },
  { key: "managers", label: "Менеджеры", icon: Users, comp: Managers },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("acts");
  const [mobileOpen, setMobileOpen] = useState(false);
  const ActiveComp = NAV.find((n) => n.key === active).comp;

  const Sidebar = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-neutral-200">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "var(--ci-accent)" }} />
        <span className="font-semibold tracking-tight">CarInspecto</span>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {NAV.map((n) => {
          const Icon = n.icon;
          const on = active === n.key;
          return (
            <button
              key={n.key}
              data-testid={`nav-${n.key}`}
              onClick={() => {
                setActive(n.key);
                setMobileOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-left transition-colors ${
                on ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
              }`}
            >
              <Icon className="w-4 h-4" style={on ? { color: "var(--ci-accent)" } : {}} />
              {n.label}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-neutral-200">
        <div className="px-3 py-2 text-xs text-neutral-500 truncate">{user?.username}</div>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Выйти
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      <aside className="hidden md:block w-64 border-r border-neutral-200 bg-white fixed inset-y-0">
        {Sidebar}
      </aside>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white border-r border-neutral-200">
            {Sidebar}
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-64">
        <header className="md:hidden h-14 bg-white border-b border-neutral-200 flex items-center px-4 gap-3 sticky top-0 z-30">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <span className="font-semibold tracking-tight">CarInspecto</span>
        </header>
        <div className="p-5 sm:p-8 max-w-6xl">
          <ActiveComp />
        </div>
      </div>
    </div>
  );
}
